(() => {
    const rootSelector = '#bls__product-bought-together';
    const visibleItemCount = 3;

    const setText = (element, value) => {
      if (element && element.textContent !== value) element.textContent = value;
    };

    const normalizeImageSource = (source) => {
      if (!source) return '';
      return source.startsWith('//') ? `https:${source}` : source;
    };

    const clampQuantity = (value) => {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isFinite(parsed)) return 1;
      return Math.min(99, Math.max(1, parsed));
    };

    const formatMoney = (cents) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.cartStrings) {
        const formatted = window.Shopify.formatMoney(cents, window.cartStrings.money_format);
        const template = document.createElement('template');
        template.innerHTML = formatted;
        return template.content.textContent.trim() || formatted;
      }

      return `$${(cents / 100).toFixed(2)}`;
    };

    const getItemParts = (item) => {
      const checkbox = item.querySelector('.bought-together-checkbox');
      const select = item.querySelector('.product-variant-option');
      const hiddenQuantity = item.querySelector('input.quantity[name="items[][quantity]"]');
      const quantityInput = item.querySelector('.merge-bundle-quantity__input');
      const option = select && select.tagName === 'SELECT'
        ? select.options[select.selectedIndex]
        : select;

      return { checkbox, select, hiddenQuantity, quantityInput, option };
    };

    const updateVariantVisual = (item) => {
      const { option } = getItemParts(item);
      if (!option) return;

      const image = item.querySelector('.merge-bundle-image img');
      const caption = item.querySelector('.merge-bundle-variant-caption');
      const title = item.querySelector('.limited-line');
      const optionText = (option.dataset.option || option.textContent || '').trim();
      const source = normalizeImageSource(option.dataset.image);

      if (image && source) {
        image.removeAttribute("srcset");
        image.removeAttribute('sizes');
        image.src = source;
        image.alt = `${title ? title.textContent.trim() : 'Product'} - ${optionText}`;
      }

      setText(caption, optionText);
    };

    const addQuantityControls = (item) => {
      const { hiddenQuantity } = getItemParts(item);
      if (!hiddenQuantity || item.querySelector('.merge-bundle-quantity')) return;

      const title = item.querySelector('.limited-line')?.textContent.trim() || 'product';
      const control = document.createElement('div');
      const decrease = document.createElement('button');
      const input = document.createElement('input');
      const increase = document.createElement('button');

      control.className = 'merge-bundle-quantity';
      decrease.type = 'button';
      decrease.dataset.quantityAction = 'decrease';
      decrease.setAttribute('aria-label', `Decrease quantity for ${title}`);
      decrease.title = 'Decrease quantity';
      decrease.textContent = '-';

      input.type = 'number';
      input.className = 'merge-bundle-quantity__input';
      input.min = '1';
      input.max = '99';
      input.step = '1';
      input.value = String(clampQuantity(hiddenQuantity.value));
      input.setAttribute('aria-label', `Quantity for ${title}`);

      increase.type = 'button';
      increase.dataset.quantityAction = 'increase';
      increase.setAttribute('aria-label', `Increase quantity for ${title}`);
      increase.title = 'Increase quantity';
      increase.textContent = '+';

      control.append(decrease, input, increase);
      item.appendChild(control);
    };

    const addVariantCaption = (item) => {
      const variantSelect = item.querySelector('.variant-select');
      if (!variantSelect || variantSelect.querySelector('.merge-bundle-variant-caption')) return;

      const caption = document.createElement('p');
      caption.className = 'merge-bundle-variant-caption';
      caption.setAttribute('aria-live', 'polite');
      variantSelect.appendChild(caption);
    };

    const setupSectionDisclosure = (bundle) => {
      const heading = bundle.querySelector('.bought-together-heading');
      const container = bundle.querySelector('.bought-together-container');
      const content = bundle.querySelector('.content-bought-together');
      if (!heading || !container || !content || heading.querySelector('.merge-fbt-heading-toggle')) return;

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'merge-fbt-heading-toggle';
      const label = document.createElement('span');
      while (heading.firstChild) label.appendChild(heading.firstChild);
      const arrow = document.createElement('span');
      arrow.className = 'merge-fbt-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      toggle.append(label, arrow);
      heading.appendChild(toggle);
      if (!container.id) container.id = 'merge-fbt-section-content';
      toggle.setAttribute('aria-controls', container.id);

      const collapse = document.createElement('button');
      collapse.type = 'button';
      collapse.className = 'merge-fbt-collapse';
      collapse.textContent = document.getElementById('merge-fbt-collapse-label')?.content.textContent.trim() || 'Collapse';
      collapse.appendChild(arrow.cloneNode(true));
      collapse.setAttribute('aria-controls', container.id);
      content.appendChild(collapse);

      const setExpanded = (expanded) => {
        bundle.dataset.mergeFbtExpanded = String(expanded);
        bundle.classList.toggle('merge-fbt-collapsed', !expanded);
        container.hidden = !expanded;
        toggle.setAttribute('aria-expanded', String(expanded));
      };
      toggle.addEventListener('click', () => setExpanded(toggle.getAttribute('aria-expanded') !== 'true'));
      collapse.addEventListener('click', () => {
        setExpanded(false);
        toggle.focus({ preventScroll: true });
        const bounds = heading.getBoundingClientRect();
        if (bounds.top < 0 || bounds.bottom > window.innerHeight) {
          heading.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      });
      setExpanded(bundle.dataset.mergeFbtExpanded === undefined
        ? true
        : bundle.dataset.mergeFbtExpanded === 'true');
      bundle.classList.add('merge-fbt-ready');
    };

    const setupDisclosure = (bundle, items) => {
      const list = bundle.querySelector('.product-bought-together-list');
      if (!list) return;

      if (!list.id) list.id = 'merge-bundle-product-list';
      let toggle = bundle.querySelector('.merge-bundle-toggle');
      const extraItems = items.filter((item, itemIndex) => itemIndex >= 3);

      if (!extraItems.length) {
        if (toggle) toggle.remove();
        return;
      }

      if (!toggle) {
        toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'merge-bundle-toggle';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', list.id);
        list.insertAdjacentElement('afterend', toggle);
      }

      if (!toggle.__mergeBundleDisclosureBound) {
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          setupDisclosure(bundle, Array.from(bundle.querySelectorAll('.product-bought-together-item')));
        });
        toggle.__mergeBundleDisclosureBound = true;
      }

      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      extraItems.forEach((item) => {
        item.classList.add('merge-bundle-extra');
        item.hidden = !expanded;
      });

      items.forEach((item) => item.classList.remove('merge-bundle-visible-last'));
      const visibleItems = expanded ? items : items.slice(0, visibleItemCount);
      const lastVisibleItem = visibleItems[visibleItems.length - 1];
      if (lastVisibleItem) lastVisibleItem.classList.add('merge-bundle-visible-last');

      setText(toggle, expanded ? 'Show fewer products' : `View more products (${extraItems.length})`);
    };

    const updateBundleState = (bundle) => {
      const items = Array.from(bundle.querySelectorAll('.product-bought-together-item'));
      const selectedItems = items.filter((item) => getItemParts(item).checkbox?.checked);
      let totalPrice = 0;

      items.forEach((item) => {
        const { checkbox, select, hiddenQuantity, quantityInput, option } = getItemParts(item);
        if (!checkbox || !select || !hiddenQuantity || !option) return;

        const selected = checkbox.checked;
        const quantity = clampQuantity(quantityInput ? quantityInput.value : hiddenQuantity.value);
        const price = Number(option.dataset.price || select.dataset.price || 0);

        item.classList.toggle('select', selected);
        select.disabled = !selected;
        hiddenQuantity.disabled = !selected;
        hiddenQuantity.value = String(quantity);

        if (quantityInput) {
          quantityInput.value = String(quantity);
          quantityInput.disabled = !selected;
        }

        item.querySelectorAll('.merge-bundle-quantity button').forEach((button) => {
          button.disabled = !selected;
        });

        if (selected) totalPrice += price * quantity;
      });

      const totalPriceNode = bundle.querySelector('.bls__total-price .heading-weight.price')
        || bundle.querySelector('.bls__total-price .price');
      const submit = bundle.querySelector('.bought-together-submit');

      setText(totalPriceNode, formatMoney(totalPrice));
      if (submit) submit.disabled = selectedItems.length === 0;
    };

    const bindBundleEvents = (bundle) => {
      if (bundle.dataset.mergeBundleBound) return;

      bundle.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-action]');
        if (!button || !bundle.contains(button)) return;

        const item = button.closest('.product-bought-together-item');
        const { hiddenQuantity, quantityInput } = getItemParts(item);
        if (!hiddenQuantity || !quantityInput) return;

        const delta = button.dataset.quantityAction === 'increase' ? 1 : -1;
        const quantity = clampQuantity(Number(quantityInput.value) + delta);
        quantityInput.value = String(quantity);
        hiddenQuantity.value = String(quantity);
        updateBundleState(bundle);
      });

      bundle.addEventListener('input', (event) => {
        const quantityInput = event.target.closest('.merge-bundle-quantity__input');
        if (!quantityInput || !bundle.contains(quantityInput)) return;

        const item = quantityInput.closest('.product-bought-together-item');
        const { hiddenQuantity } = getItemParts(item);
        const parsed = Number.parseInt(quantityInput.value, 10);
        if (hiddenQuantity && Number.isFinite(parsed) && parsed >= 1) {
          hiddenQuantity.value = String(Math.min(99, parsed));
          updateBundleState(bundle);
        }
      });

      bundle.addEventListener('change', (event) => {
        const item = event.target.closest('.product-bought-together-item');
        if (!item || !bundle.contains(item)) return;

        if (event.target.matches('.product-variant-option')) updateVariantVisual(item);

        if (event.target.matches('.merge-bundle-quantity__input')) {
          const { hiddenQuantity, quantityInput } = getItemParts(item);
          const quantity = clampQuantity(quantityInput.value);
          quantityInput.value = String(quantity);
          hiddenQuantity.value = String(quantity);
        }

        updateBundleState(bundle);
        window.setTimeout(() => updateBundleState(bundle), 80);
      });

      bundle.dataset.mergeBundleBound = 'true';
    };

    const arrangeBoughtTogether = () => {
      const bundle = document.querySelector(rootSelector);
      const productForm = document.querySelector('.bls__product-details-infor #product-form');
      if (!bundle || !productForm) return;

      if (bundle.previousElementSibling !== productForm) {
        productForm.insertAdjacentElement('afterend', bundle);
      }

      const container = bundle.querySelector('.bought-together-container');
      const imageWrapper = bundle.querySelector('.products-wrapper');
      const info = bundle.querySelector('.bls__info-bought-together');
      const total = bundle.querySelector('.box-total');

      if (container && info && total && info.parentElement !== container) {
        container.insertBefore(info, total);
      }

      const imageItems = Array.from(bundle.querySelectorAll('.products-wrapper .product-bought-image-item'));
      const infoItems = Array.from(bundle.querySelectorAll('.product-bought-together-item'));

      infoItems.forEach((item, index) => {
        const regularPrice = item.querySelector('.info-price .price__regular');

        if (regularPrice && !regularPrice.querySelector('.price')) {
          const priceValue = document.createElement('span');
          priceValue.className = 'price';

          if (regularPrice.classList.contains('special-price')) {
            priceValue.classList.add('special-price');
          }

          while (regularPrice.firstChild) priceValue.appendChild(regularPrice.firstChild);
          regularPrice.classList.remove('price', 'special-price');
          regularPrice.appendChild(priceValue);
        }

        const imageItem = imageItems[index];
        if (imageItem && !item.querySelector('.merge-bundle-image')) {
          const image = imageItem.querySelector('.bls__img-url');
          if (image) {
            const visual = document.createElement('div');
            visual.className = 'merge-bundle-image';
            visual.appendChild(image.cloneNode(true));
            item.insertBefore(visual, item.firstElementChild);
          }
        }

        const checkbox = item.querySelector('.bought-together-checkbox');
        const title = item.querySelector('.limited-line')?.textContent.trim() || `product ${index + 1}`;
        if (checkbox) checkbox.setAttribute('aria-label', `Select ${title}`);

        addVariantCaption(item);
        addQuantityControls(item);
        updateVariantVisual(item);
      });

      setupDisclosure(bundle, infoItems);
      setupSectionDisclosure(bundle);
      bindBundleEvents(bundle);
      updateBundleState(bundle);

      if (imageWrapper) imageWrapper.setAttribute('aria-hidden', 'true');
      bundle.classList.add('merge-lucia-bundle');
    };

    window.mergeArrangeBoughtTogether = arrangeBoughtTogether;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', arrangeBoughtTogether, { once: true });
    } else {
      arrangeBoughtTogether();
    }

    document.addEventListener('shopify:section:load', arrangeBoughtTogether);

    const observer = new MutationObserver(arrangeBoughtTogether);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  })();
